import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Container, Paper, Stack, Typography } from '@mui/material';
import { CheckCircle as CheckCircleIcon, ErrorOutline as ErrorOutlineIcon } from '@mui/icons-material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { verifyEmail } from '../api/authApi';
import { useAuth } from '../auth/AuthContext';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('Checking your verification link...');
  const { refreshUser, user } = useAuth();

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('This verification link looks incomplete. Try opening the link from your email again, or request a new one.');
      return;
    }

    verifyEmail(token)
      .then(() => {
        setStatus('success');
        setMessage('Your email has been verified.');
        refreshUser().catch(() => {});
      })
      .catch((error) => {
        if (user?.email_verified) {
          setStatus('success');
          setMessage('Your email is already verified.');
          return;
        }
        setStatus('error');
        setMessage(error.message || 'This verification link could not be confirmed. It may have expired.');
      });
  }, [searchParams, refreshUser, user?.email_verified]);

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={0} sx={{ p: 4, border: 1, borderColor: 'divider', textAlign: 'center' }}>
        <Stack spacing={2.5} alignItems="center">
          {status === 'verifying' && <CircularProgress />}
          {status === 'success' && <CheckCircleIcon color="success" sx={{ fontSize: 52 }} />}
          {status === 'error' && <ErrorOutlineIcon color="error" sx={{ fontSize: 52 }} />}
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
              Email verification
            </Typography>
            <Typography color="text.secondary">{message}</Typography>
          </Box>
          {status !== 'verifying' && (
            <Alert severity={status === 'success' ? 'success' : 'error'} sx={{ width: '100%', textAlign: 'left' }}>
              {status === 'success' ? (
                'You can return to Qode Clarity and continue setup.'
              ) : (
                <>
                  Ask for a new verification email from{' '}
                  <RouterLink to="/my-learning" style={{ color: 'inherit', fontWeight: 700 }}>
                    My Learning
                  </RouterLink>{' '}
                  after signing in.
                </>
              )}
            </Alert>
          )}
          <Button component={RouterLink} to="/my-learning" variant="contained">
            Go to My Learning
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
};

export default VerifyEmailPage;
